<script>
import { attendanceTypesAPI } from '../../../api/attendanceTypes';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';

export default {
  name: 'AttendanceTypesView',
  data() {
    return {
      types: [],
      loading: true,
      showModal: false,
      isSaving: false,
      form: this.getEmptyForm(),
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    isEditing() {
      return !!this.form.id;
    },
  },
  mounted() {
    this.fetchTypes();
  },
  methods: {
    getEmptyForm() {
      return { id: null, description: '', active: true };
    },
    async fetchTypes() {
      this.loading = true;
      try {
        const { data } = await attendanceTypesAPI.getAll(this.accountId, false);
        this.types = data || [];
      } catch {
        useAlert(this.$t('ATTENDANCE_TYPES.ERRORS.FETCH'));
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.form = this.getEmptyForm();
      this.showModal = true;
    },
    openEditModal(type) {
      this.form = { id: type.id, description: type.description, active: type.active };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.form = this.getEmptyForm();
    },
    async saveType() {
      if (!this.form.description.trim()) {
        useAlert(this.$t('ATTENDANCE_TYPES.FORM.DESCRIPTION_REQUIRED'));
        return;
      }
      this.isSaving = true;
      const payload = {
        description: this.form.description.trim(),
        active: this.form.active,
      };
      try {
        if (this.isEditing) {
          await attendanceTypesAPI.update(this.accountId, this.form.id, payload);
          useAlert(this.$t('ATTENDANCE_TYPES.UPDATED'));
        } else {
          await attendanceTypesAPI.create(this.accountId, payload);
          useAlert(this.$t('ATTENDANCE_TYPES.SAVED'));
        }
        this.closeModal();
        await this.fetchTypes();
      } catch {
        useAlert(
          this.$t(this.isEditing ? 'ATTENDANCE_TYPES.ERRORS.UPDATE' : 'ATTENDANCE_TYPES.ERRORS.SAVE')
        );
      } finally {
        this.isSaving = false;
      }
    },
    async toggleActive(type) {
      try {
        await attendanceTypesAPI.update(this.accountId, type.id, {
          description: type.description,
          active: !type.active,
        });
        await this.fetchTypes();
      } catch {
        useAlert(this.$t('ATTENDANCE_TYPES.ERRORS.UPDATE'));
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-full px-6 py-6 overflow-y-auto bg-slate-50">
    <div class="flex items-center justify-between pb-6">
      <h1 class="text-2xl font-bold text-slate-800">{{ $t('ATTENDANCE_TYPES.TITLE') }}</h1>
      <button
        class="px-4 py-2 font-medium text-white transition-colors rounded-md shadow-sm bg-fluxi-primary hover:bg-fluxi-primary/90"
        @click="openAddModal"
      >
        {{ $t('ATTENDANCE_TYPES.ADD_BUTTON') }}
      </button>
    </div>

    <div class="overflow-x-auto bg-white border rounded-lg shadow-sm border-slate-200">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-slate-50/50">
          <tr>
            <th class="px-6 py-4 text-xs font-semibold tracking-wider text-left uppercase text-slate-500">{{ $t('ATTENDANCE_TYPES.TABLE.DESCRIPTION') }}</th>
            <th class="px-6 py-4 text-xs font-semibold tracking-wider text-left uppercase text-slate-500">{{ $t('ATTENDANCE_TYPES.TABLE.STATUS') }}</th>
            <th class="px-6 py-4 text-xs font-semibold tracking-wider text-center uppercase text-slate-500">{{ $t('ATTENDANCE_TYPES.TABLE.ACTIONS') }}</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr v-if="loading">
            <td colspan="3" class="px-6 py-8 text-sm text-center text-slate-500">{{ $t('ATTENDANCE_TYPES.TABLE.LOADING') }}</td>
          </tr>
          <tr v-else-if="types.length === 0">
            <td colspan="3" class="px-6 py-8 text-sm text-center text-slate-500">{{ $t('ATTENDANCE_TYPES.TABLE.EMPTY') }}</td>
          </tr>
          <template v-else>
            <tr v-for="type in types" :key="type.id" class="transition-colors hover:bg-slate-50/50">
              <td class="px-6 py-4 text-sm font-medium text-slate-700">{{ type.description }}</td>
              <td class="px-6 py-4 text-sm">
                <span
                  class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full"
                  :class="type.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'"
                >
                  {{ type.active ? $t('ATTENDANCE_TYPES.TABLE.ACTIVE') : $t('ATTENDANCE_TYPES.TABLE.INACTIVE') }}
                </span>
              </td>
              <td class="px-6 py-4 space-x-2 text-sm font-medium text-center whitespace-nowrap">
                <button
                  class="inline-flex items-center px-3 py-1 text-xs font-medium transition-colors rounded text-fluxi-primary bg-fluxi-primary/10 hover:bg-fluxi-primary/20"
                  @click="openEditModal(type)"
                >
                  {{ $t('ATTENDANCE_TYPES.TABLE.EDIT') }}
                </button>
                <button
                  class="inline-flex items-center px-3 py-1 text-xs font-medium text-amber-700 transition-colors rounded bg-amber-50 hover:bg-amber-100"
                  @click="toggleActive(type)"
                >
                  {{ type.active ? $t('ATTENDANCE_TYPES.TABLE.DEACTIVATE') : $t('ATTENDANCE_TYPES.TABLE.ACTIVATE') }}
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 class="text-lg font-bold text-slate-800">
            {{ isEditing ? $t('ATTENDANCE_TYPES.MODAL.EDIT_TITLE') : $t('ATTENDANCE_TYPES.MODAL.ADD_TITLE') }}
          </h2>
          <button class="text-xl font-bold text-slate-400 hover:text-slate-600" @click="closeModal">
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="flex-grow px-6 py-4">
          <label class="block mb-1 text-sm font-semibold text-slate-700">{{ $t('ATTENDANCE_TYPES.FORM.DESCRIPTION') }}</label>
          <input
            v-model="form.description"
            type="text"
            class="w-full px-3 py-2 border rounded outline-none border-slate-300 focus:ring-2 focus:ring-fluxi-primary"
          />
          <label class="flex items-center gap-2 mt-4 text-sm font-medium text-slate-700">
            <input v-model="form.active" type="checkbox" />
            {{ $t('ATTENDANCE_TYPES.FORM.ACTIVE') }}
          </label>
        </div>

        <div class="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            class="px-4 py-2 font-medium bg-white border rounded text-slate-600 border-slate-300 hover:bg-slate-50"
            @click="closeModal"
          >
            {{ $t('ATTENDANCE_TYPES.MODAL.CANCEL') }}
          </button>
          <button
            :disabled="isSaving"
            class="px-6 py-2 font-medium text-white rounded bg-fluxi-primary hover:bg-fluxi-primary/90 disabled:opacity-50"
            @click="saveType"
          >
            {{ isSaving ? $t('ATTENDANCE_TYPES.MODAL.SAVING') : $t('ATTENDANCE_TYPES.MODAL.SAVE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
